import { useNavigate } from 'react-router-dom';
import { SmartLink } from '@/components/ui/SmartLink';
import { Star, CheckCircle, Shield, Search, MapPin, ArrowRight, Users, Clock, TrendingUp } from 'lucide-react';
import {
  WrenchScrewdriverIcon,
  BoltIcon,
  PaintBrushIcon,
  SparklesIcon,
  WrenchIcon,
  BuildingOfficeIcon,
  TruckIcon,
  CogIcon
} from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { backendJobService } from '@/services/backendJobService';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { SkeletonCard } from '@/components/ui/SkeletonLoaders';
import { EnhancedEmptyState } from '@/components/ui/EnhancedEmptyState';
import { FadeIn } from '@/components/ui/AnimatedComponents';

export function HomePage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<any[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredCategories, setFilteredCategories] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<any | null>(null);
  const [serviceError, setServiceError] = useState('');

  // Load service categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setIsLoadingCategories(true);
        const homepageCategories = await backendJobService.getHomepageCategories();

        if (homepageCategories && homepageCategories.length > 0) {
          const mappedCategories = homepageCategories.map(cat => ({
            id: cat.id,
            name: cat.name,
            icon: getHeroIcon(cat.icon_name),
            count: cat.job_count,
            description: cat.description
          }));
          setCategories(mappedCategories);
        }
      } catch (error) {
        console.error('Failed to load categories', error);
      } finally {
        setIsLoadingCategories(false);
      }
    };
    loadCategories();
  }, []);

  // Filter categories as user types
  useEffect(() => {
    if (searchQuery.length >= 2) {
      const filtered = categories.filter(cat =>
        cat.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredCategories(filtered);
      setShowDropdown(filtered.length > 0);
    } else {
      setFilteredCategories([]);
      setShowDropdown(false);
    }
  }, [searchQuery, categories]);

  const getHeroIcon = (iconName: string) => {
    const iconMap: { [key: string]: any } = {
      'WrenchScrewdriver': WrenchScrewdriverIcon,
      'Bolt': BoltIcon,
      'PaintBrush': PaintBrushIcon,
      'Sparkles': SparklesIcon,
      'Wrench': WrenchIcon,
      'BuildingOffice': BuildingOfficeIcon,
      'Truck': TruckIcon,
      'Cog': CogIcon
    };
    return iconMap[iconName] || CogIcon;
  };

  const handleSearch = () => {
    if (!searchQuery.trim() && !selectedCategory) {
      setServiceError('Please enter what service you need');
      return;
    }
    setServiceError('');
    // If user selected a category from dropdown, navigate to create job with pre-filled category
    if (selectedCategory) {
      navigate('/create-job', { state: { prefilledCategory: selectedCategory.id, categoryName: selectedCategory.name } });
    }
    // If user typed but didn't select, try to match a category
    else if (searchQuery.trim()) {
      const matchedCategory = categories.find(cat =>
        cat.name.toLowerCase() === searchQuery.toLowerCase().trim()
      );
      if (matchedCategory) {
        navigate('/create-job', { state: { prefilledCategory: matchedCategory.id, categoryName: matchedCategory.name } });
      } else {
        navigate('/create-job');
      }
    } else {
      navigate('/create-job');
    }
  };

  const handleCategorySelect = (category: any) => {
    setSelectedCategory(category);
    setSearchQuery(category.name);
    setShowDropdown(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Skip Links */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:shadow-lg"
      >
        Skip to main content
      </a>

      {/* HERO SECTION - Professional & Clean */}
      <section className="relative bg-gradient-to-br from-primary-50 via-white to-primary-50/30 pt-16 pb-12 sm:pb-16 lg:pt-24 lg:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">

            {/* Main Headline */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 leading-tight">
              Find Trusted Local
              <span className="block text-primary-600">Service Providers</span>
            </h1>

            {/* Subheadline */}
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed px-4">
              Connect with verified professionals for home repairs, maintenance, and improvements. Get quotes, compare, and hire with confidence.
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto mb-6 sm:mb-8 px-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400 z-10 pointer-events-none" aria-hidden="true" />
                  <input
                    type="text"
                    placeholder="What service do you need?"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setSelectedCategory(null);
                      if (e.target.value.trim()) setServiceError('');
                    }}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    onFocus={() => searchQuery.length >= 2 && setShowDropdown(true)}
                    onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                    className={`w-full pl-10 sm:pl-12 pr-4 py-3 sm:py-4 text-sm sm:text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent shadow-sm ${serviceError ? 'border-red-400' : 'border-gray-300'}`}
                    aria-label="Search for services"
                    aria-autocomplete="list"
                    aria-controls="service-suggestions"
                    aria-expanded={showDropdown}
                  />
                  
                  {/* Autocomplete Dropdown */}
                  {showDropdown && filteredCategories.length > 0 && (
                    <div 
                      id="service-suggestions"
                      className="absolute z-20 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto"
                      role="listbox"
                      aria-label="Service suggestions"
                    >
                      {filteredCategories.map((category) => {
                        const IconComponent = category.icon;
                        return (
                          <button
                            key={category.id}
                            onClick={() => handleCategorySelect(category)}
                            className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 border-b border-gray-100 last:border-b-0 transition-colors"
                          >
                            <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                              <IconComponent className="w-5 h-5 text-primary-600" />
                            </div>
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">{category.name}</div>
                              {category.description && (
                                <div className="text-sm text-gray-500 truncate">{category.description}</div>
                              )}
                            </div>
                            {category.count > 0 && (
                              <div className="text-xs text-gray-400">{category.count} pros</div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Selected Category Indicator */}
                  {selectedCategory && (
                    <div className="absolute right-4 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-success-600" />
                      <span className="text-sm text-success-600 font-medium">Selected</span>
                    </div>
                  )}

                  {serviceError && (
                    <p className="text-red-500 text-sm mt-1 ml-1">{serviceError}</p>
                  )}
                </div>
                <Button
                  size="lg"
                  onClick={handleSearch}
                  icon={<ArrowRight aria-hidden="true" />}
                  iconPosition="right"
                  className="whitespace-nowrap w-full sm:w-auto"
                  aria-label={selectedCategory ? `Post job for ${selectedCategory.name}` : 'Search for services'}
                >
                  {selectedCategory ? 'Post Job' : 'Search'}
                </Button>
              </div>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-xs sm:text-sm text-gray-600 px-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-success-600 flex-shrink-0" aria-hidden="true" />
                <span>Verified Professionals</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-primary-600 flex-shrink-0" aria-hidden="true" />
                <span>Secure Payments</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 sm:w-5 sm:h-5 text-warning-500 fill-current flex-shrink-0" aria-hidden="true" />
                <span>Rated & Reviewed</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS SECTION */}
      <section className="py-8 sm:py-12 bg-white" id="main-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
              Get Started in 3 Simple Steps
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-4">
              Finding the right professional for your job has never been easier
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            {/* Step 1 */}
            <Card padding="lg" hover>
              <div className="text-center">
                <div className="w-14 h-14 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-primary-600">1</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Post Your Job
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Describe what you need done. It's free and takes just a few minutes.
                </p>
              </div>
            </Card>

            {/* Step 2 */}
            <Card padding="lg" hover>
              <div className="text-center">
                <div className="w-14 h-14 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-primary-600">2</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Compare Quotes
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Receive quotes from verified professionals. Review ratings and profiles.
                </p>
              </div>
            </Card>

            {/* Step 3 */}
            <Card padding="lg" hover>
              <div className="text-center">
                <div className="w-14 h-14 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-primary-600">3</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Hire with Confidence
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Choose the best pro for your needs and get the job done right.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* POPULAR SERVICES SECTION */}
      <section className="py-8 sm:py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
              Popular Services
            </h2>
            <p className="text-base sm:text-lg text-gray-600 px-4">
              Browse by category to find the right professional
            </p>
          </div>

          {isLoadingCategories ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
              {[...Array(8)].map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
              {categories.slice(0, 8).map((category, index) => {
                const IconComponent = category.icon;
                return (
                  <FadeIn key={category.id} delay={index * 50}>
                    <Card
                      padding="md"
                      hover
                      onClick={() => navigate('/create-job', { state: { category: category.name } })}
                      className="cursor-pointer"
                    >
                      <div className="text-center">
                        <div className="w-12 h-12 bg-primary-50 rounded-lg flex items-center justify-center mx-auto mb-3">
                          <IconComponent className="w-6 h-6 text-primary-600" />
                        </div>
                        <h3 className="font-semibold text-gray-900 mb-1">
                          {category.name}
                        </h3>
                        {category.count > 0 && (
                          <p className="text-xs text-gray-500">
                            {category.count} providers
                          </p>
                        )}
                      </div>
                    </Card>
                  </FadeIn>
                );
              })}
            </div>
          )}

          <div className="text-center mt-8">
            <Button variant="outline" onClick={() => navigate('/create-job')}>
              View All Services
            </Button>
          </div>
        </div>
      </section>

      {/* STATS SECTION */}
      <section className="py-8 sm:py-10 bg-primary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 text-center text-white">
            <div>
              <div className="text-2xl sm:text-3xl font-bold mb-1">10,000+</div>
              <div className="text-primary-100 text-xs sm:text-sm">Jobs Completed</div>
            </div>
            <div>
              <div className="text-2xl sm:text-3xl font-bold mb-1">5,000+</div>
              <div className="text-primary-100 text-xs sm:text-sm">Verified Pros</div>
            </div>
            <div>
              <div className="text-2xl sm:text-3xl font-bold mb-1">4.8/5</div>
              <div className="text-primary-100 text-xs sm:text-sm">Average Rating</div>
            </div>
            <div>
              <div className="text-2xl sm:text-3xl font-bold mb-1">24/7</div>
              <div className="text-primary-100 text-xs sm:text-sm">Support Available</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-8 sm:py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-base sm:text-lg text-gray-600 mb-4 sm:mb-6 px-4">
            Join thousands of homeowners who trust RapidFix for their service needs
          </p>
          <div className="flex justify-center px-4">
            <Button size="lg" onClick={() => navigate('/create-job')}>
              Post a Job - It's Free
            </Button>
          </div>
        </div>
      </section>

      {/* FOR PROFESSIONALS SECTION */}
      <section className="py-8 sm:py-12 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-10 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">
                Are You a Service Professional?
              </h2>
              <p className="text-gray-300 text-base sm:text-lg mb-4 sm:mb-6 leading-relaxed">
                Grow your business by connecting with customers in your area. Get access to job leads, manage your schedule, and build your reputation.
              </p>
              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-success-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold mb-1">No Upfront Costs</div>
                    <div className="text-gray-400 text-sm">Only pay when you get hired</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Users className="w-6 h-6 text-primary-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold mb-1">Build Your Reputation</div>
                    <div className="text-gray-400 text-sm">Collect reviews and showcase your work</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <TrendingUp className="w-6 h-6 text-warning-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold mb-1">Grow Your Business</div>
                    <div className="text-gray-400 text-sm">Access job leads in your area</div>
                  </div>
                </div>
              </div>
              <Button
                size="lg"
                variant="primary"
                onClick={() => navigate('/signup?type=provider')}
              >
                Join as a Professional
              </Button>
            </div>
            <div className="hidden md:block">
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <div className="text-sm text-gray-400 mb-2">Success Story</div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center text-xl font-bold">
                    JD
                  </div>
                  <div>
                    <div className="font-semibold">John's Plumbing</div>
                    <div className="flex items-center gap-1 text-sm text-gray-400">
                      <Star className="w-4 h-4 text-warning-500 fill-current" />
                      <span>4.9 (127 reviews)</span>
                    </div>
                  </div>
                </div>
                <p className="text-gray-300 italic">
                  "RapidFix has transformed my business. I've doubled my client base in just 6 months and the quality of leads is excellent."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}